import { FC } from 'react';

import { useMailSequenceSkipScheduledSendMutation } from '../../../../generated/graphql';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { customToast } from '../../../components/ToastAlert/customToast';

interface Props {
  isOpen: boolean;
  scheduledSendId: string | null;
  onClose: () => void;
  /** Called after a successful skip (refetch enrollments etc.). */
  onSkipped: () => void;
}

/** Confirm dialog wrapping the skip-scheduled-send mutation. */
const SkipMailConfirmModal: FC<Props> = ({
  isOpen,
  scheduledSendId,
  onClose,
  onSkipped,
}) => {
  const [skipMutation, { loading: skipping }] =
    useMailSequenceSkipScheduledSendMutation({
      onCompleted: ({ mailSequenceSkipScheduledSend }) => {
        if (mailSequenceSkipScheduledSend?.success) {
          customToast.success('Mail skipped.');
          onClose();
          onSkipped();
        } else {
          customToast.error(
            mailSequenceSkipScheduledSend?.error || 'Could not skip this mail.',
          );
        }
      },
      onError: (err) =>
        customToast.error(err.message || 'Could not skip this mail.'),
    });

  return (
    <Modal isOpen={isOpen} setClosed={onClose} width="small">
      <div className="px-10 py-8">
        <div className="flex flex-col items-center">
          <div className="mb-2 flex text-center font-serif text-subtitle-small text-green-150">
            Skip Mail?
          </div>
          <div className="text-center text-body text-green-125">
            This Mail will be cancelled and skipped. The rest of the Mail
            sequence keeps its schedule.
          </div>
        </div>
        <div className="mt-11 flex w-full flex-row items-center justify-center">
          <Button
            title="Keep Mail"
            theme="secondary"
            className="mr-5"
            onClick={onClose}
            disabled={skipping}
          />
          <Button
            title={skipping ? 'Skipping...' : 'Skip Mail'}
            theme="primary"
            disabled={skipping}
            onClick={() => {
              if (!scheduledSendId) return;
              skipMutation({
                variables: { scheduledSendId },
              });
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SkipMailConfirmModal;
